import React, { useEffect, useRef, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import CSVTemplate from '../../components/csvTemplate';
import CustomButton from '../../components/customButton';
import { hasData } from '../sites/multipleSitesCreation/utils';
import { setAlert } from '../../redux/dashboard/actions';
import { useDispatch } from 'react-redux';
import API from '../../services/api';
import utils from '../../utils';

const UploadFileModal = ({ isOpen, onCancel, toggle, portal, confirmCallback }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('point_of_sale_data.csv');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setFileName('point_of_sale_data.csv');
      setError(null);
      setIsLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setError(null);

    if (file) {
      if (file.type === 'text/csv') {
        hasData(file).then(hasContent => {
          setError(null);
          setFileName(file.name);
          if (!hasContent) {
            setError('The CSV file is empty or does not contain data rows.');
          }
        });
      } else {
        setError('File choosen is not a CSV!');
        setFileName(file.name);
      }
    } else {
      setFileName('point_of_sale_data.csv');
      setError(null);
    }
  }, [file]);

  const exportPosCSV = () => {
    const headers = [
      'site_id',
      'product_name',
      'labels',
      'ref_id',
      'sku',
      'qty',
      'value_per_unit',
      'total_value',
      'timestamp'
    ];

    const rows = [].map(report => {
      const labels = Array.isArray(report.labels) ? report.labels.join(';') : report.labels || '';

      const siteId = report.site_id || report.siteId || '';
      const productName = report.product_name || report.productName || '';
      const refId = report.ref_id || report.refId || '';
      const sku = report.sku || '';
      const quantity = report.quantity || report.qty || '';
      const valuePerUnit = report.value_per_unit || report.valuePerUnit || '';
      const totalValue = report.total_value || report.totalValue || '';
      const timestamp = report.timestamp || report.date || '';

      return [
        siteId,
        '"' + productName + '"',
        '"' + labels + '"',
        '"' + refId + '"',
        '"' + sku + '"',
        quantity,
        valuePerUnit,
        totalValue,
        '"' + timestamp + '"'
      ];
    });

    // Example row to show expected format (first data row after headers)
    const exampleRow = [
      's0123456789_example',
      '"Sparkling Water 12oz"',
      '"beverage;cold;front-counter"',
      '"POS-1001"',
      '"SKU-WATER-12"',
      2,
      1.5,
      3,
      '"2026-05-10T09:15:00Z"'
    ];

    const csvContent = [headers]
      .concat([exampleRow])
      .concat(rows)
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'posReport.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderInfo = () => {
    if (error || !file) return null;

    return (
      <div className="file-info">
        {'File loaded'}: <b>{fileName}</b>.
      </div>
    );
  };

  const handleUploadFile = () => {
    if (file) {
      setIsLoading(true);
      API.createPosReport(portal.id)
        .then(response => {
          API.uploadPosFile(response.uploadUrl, file)
            .then(() => {
              dispatch(
                setAlert(
                  utils.generateAlert(
                    'File uploaded successfully, you will get notified once processing is complete.',
                    'success'
                  )
                )
              );
            })
            .catch(err => {
              dispatch(setAlert(utils.generateAlert('Error uploading file: ' + err.message, 'error')));
            })
            .finally(() => {
              setIsLoading(false);
              toggle(false);
              if (confirmCallback) {
                confirmCallback();
              }
            });
        })
        .catch(err => {
          dispatch(setAlert(utils.generateAlert('Error creating upload URL: ' + err.message, 'error')));
        })
        .finally(() => {
          setIsLoading(false);
          toggle(false);
          if (confirmCallback) {
            confirmCallback();
          }
        });
    }
  };

  return (
    <Modal size="lg" isOpen={isOpen} fade={false} centered id="upload-files-modal">
      <ModalHeader toggle={onCancel ? onCancel : () => toggle(false)}>
        <h2>Upload your Point of Sale Data</h2>
        <div className="modal-subtitle">
          &nbsp;&nbsp;&nbsp;&nbsp;AdMobilize needs each the correct information to read the file. Download the CSV
          model, look at the example and then upload the real file. We use comma &quot;,&quot; as CSV column separator.
        </div>
      </ModalHeader>
      <ModalBody>
        <CSVTemplate
          fileName={'point_of_sale_data.csv'}
          columns={[
            'product_name',
            'labels',
            'ref_id',
            'sku',
            'quantity',
            'value_per_unit',
            'total_value',
            'timestamp'
          ]}
        />
        {renderInfo()}
        {error && <div className="error-message">{error}</div>}
        <div className="file-input-container">
          <CustomButton
            title="Download Template"
            classes="btn-secondary"
            icon="download"
            handleClick={() => {
              exportPosCSV();
            }}
          />
          <CustomButton
            title="Choose File"
            classes="btn-secondary"
            icon="folder"
            handleClick={() => {
              inputRef.current.click();
            }}
          />
          <input id="input-file-btn" type="file" ref={inputRef} onChange={e => setFile(e.target.files[0])} />
        </div>
      </ModalBody>
      <ModalFooter>
        <CustomButton
          title="Cancel"
          classes="btn-secondary"
          handleClick={() => {
            toggle(false);
          }}
        />
        <CustomButton
          title={'Upload File'}
          classes="btn-primary"
          disabled={error || !file}
          loading={isLoading}
          handleClick={() => {
            handleUploadFile();
          }}
        />
      </ModalFooter>
    </Modal>
  );
};

export default UploadFileModal;
